define(["mw-log/impl/Modes",
        "mw-log/impl/Levels",
        "mw-log/impl/Backlog",
        "mw-log/util/LogUtils"],
    function (Modes, Levels, Backlog, LogUtils) {

    const defaultName = "DEFAULT";
    const backlogSize = 100; // backlog will save 100 most recent messages
    const backlog = Backlog.create(backlogSize);
    const levelOf = {
        debug:4,
        log:3,
        dir:3,
        table:3,
        trace:3,
        time:3,
        timeEnd:3,
        group:3,
        groupCollapsed:3,
        groupEnd:3,
        info:2,
        warn:1,
        assert:0,
        error:0
    };

    let callbacks = {};
    let nextCallbackId = 0;
    let globalThreshold = Levels.DEBUG;
    let mode = Modes.DEVELOPMENT;
    let groupStack = [];
    let loggers = {};
    /** Class for logging messages to console */
    class Log {
        constructor(name){
            if(loggers[name] != null){
                loggers[defaultName].error(
                    "a logger with the name '" + name + "' already exists - " +
                    "change the name to prevent interference"
                );
                // return empty object (no properties have been declared)
                return;
            }

            LogUtils.makeReadOnlyProperty(this, "name", name);
            loggers[this.name] = this;

            this._timers = {};
            this._threshold = Levels.DEBUG;
            this._showTimestamp = true;

            LogUtils.makeReadOnlyProperty(this, "modes", Modes);
            LogUtils.makeReadOnlyProperty(this, "levels", Levels);
        };

        static _sanitizeLevel(level){
            return typeof level === 'number' ? level : Levels.DEBUG;
        }

        static _sanitizeMode(mode){
            return LogUtils.objectValues(Modes).indexOf(mode) > -1 ? mode : Modes.development;
        }

        get threshold(){
            return this._threshold;
        }
        set threshold(value){
            this._threshold = Log._sanitizeLevel(value);
        }

        get showTimestamp(){
            return this._showTimestamp;
        }
        set showTimestamp(value){
            this._showTimestamp = !!value;
        }

        get globalThreshold(){
            return globalThreshold;
        }
        set globalThreshold(value){
            globalThreshold = Log._sanitizeLevel(value);
        }

        get mode(){
            return mode;
        }
        set mode(value){
            mode = Log._sanitizeMode(value);
        }

        _shouldLog(level) {
            return globalThreshold > level && this._threshold > level;
        }

        _nameAndTime(){
            let label = [];
            if(this.name !== defaultName){
                label.push(this.name);
            }
            if(this._showTimestamp){
                label.push(LogUtils.currentTimeString());
            }
            return label;
        }

        /**
         * Displays a table
         * @param {array} data - will display as a table. also accepts objects
         * with named parameters
         * @example
         * logger.table([[1,2,3],[4,5,6],[7,8,9]]);
         * logger.table([{x:0,y:1},{x:1,y:0}]);
         * logger.table({name:"Vincent",score:99.5,inventory:["pliers","string","tape"]});
         */
        table(data){
            if(this._shouldLog(levelOf.table)){
                const message = {
                    namespace:this.name,
                    type:"table",
                    message:[data],
                    time:new Date()
                };
                backlog.add(message);
                // name and time on a separate line, because the first arg must be data for table
                LogUtils.printToConsole("log", this._nameAndTime(), mode);
                LogUtils.printToConsole("table", [data], mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        /**
         * Displays a stack trace
         * @example
         * logger.trace();
         */
        trace(){
            if(this._shouldLog(levelOf.trace)){
                const message = {namespace:this.name, type:"trace", message:null, time:new Date()};
                const e = new Error();
                let trace = e.stack.split("\n");
                // remove "Error" in chrome
                if (trace[0] === "Error") {
                    trace.shift();
                }
                // remove trace into mw-log
                // firefox & chrome   edge safari
                while (trace[0].includes("Log.js") || trace[0].includes("logger.trace") ||
                    trace[0] === "trace") {
                    trace.shift();
                }
                trace = trace.join("\n");
                message.message = [trace];

                backlog.add(message);
                LogUtils.printToConsole("log", this._nameAndTime(), mode);
                LogUtils.printToConsole("trace",[], mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        /**
         * Starts a timer
         * @param {string} label - label for the timer
         * @example
         * logger.time("my timer");
         * // code that is being timed
         * logger.timeEnd("my timer");
         */
        time(label){
            if(this._shouldLog(levelOf.time)){
                if (this._timers[label] == null) {
                    this._timers[label] = new Date().getTime();
                } else {
                    this.warn('timer "' + label + '" already exists.');
                }
            }
        }

        /**
         * Ends a timer and displays the time since start
         * @param {string} label - label for the timer
         * @example
         * logger.time("my timer");
         * // code that is being timed
         * logger.timeEnd("my timer");
         */
        timeEnd(label){
            if(this._shouldLog(levelOf.timeEnd)) {
                const message = {
                    namespace:this.name,
                    type:"timeEnd",
                    message:null,
                    time:new Date()
                };
                if (this._timers[label] != null) {
                    const delta = new Date().getTime() - this._timers[label];
                    message.message = [label + ": " + delta + "ms - timer ended"];
                    backlog.add(message);
                    LogUtils.printToConsole("log",
                        this._nameAndTime().concat(message.message), mode);
                    LogUtils.execCallbacks(callbacks, message);
                    this._timers[label] = null;
                } else {
                    this.warn('timer "' + label + '" doesn\'t exist.');
                }
            }
        }

        _group(label, collapsed){
            if(this._shouldLog(levelOf.group)){
                const method = collapsed ? "groupCollapsed" : "group";
                const message = {
                    namespace:this.name,
                    type:method,
                    message:[label],
                    time:new Date()};
                // store the label of the group
                groupStack.push(label);
                backlog.add(message);
                LogUtils.printToConsole(method, [label], mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        /**
         * Starts a console message group in a collapsed state
         * @param {string} label - name of group
         * @example
         * logger.groupCollapsed("my group");
         * logger.log("this message is hidden in the collapsed group");
         * logger.groupEnd();
         */
        groupCollapsed(label){
            this._group(label, true);
        }

        /**
         * Starts a console message group in an expanded state
         * @param {string} label - name of group
         * @example
         * logger.group("my group");
         * logger.log("this message is visible in 'my group' ");
         * logger.groupEnd();
         */
        group(label){
            this._group(label,false);
        }

        /**
         * Ends the current console message group
         * @example
         * logger.group("my group");
         * logger.log("hello!");
         * logger.groupEnd();
         */
        groupEnd(){
            if(this._shouldLog(levelOf.groupEnd)){
                // clear the current group from the stack
                const groupName = groupStack.pop();
                const message = {
                    namespace:this.name,
                    type:"groupEnd",
                    message:[groupName],
                    time:new Date()
                };
                backlog.add(message);
                LogUtils.printToConsole("groupEnd", [], mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        /**
         * Display an error if the condition is false
         * @param {boolean} condition - condition being tested
         * @param {string} description - sent as an error if the condition is false
         * @example
         * logger.assert(1 > 0, "the condition is true so this message will not be shown");
         * logger.assert(1 < 0, "the condition is false so this message will be shown");
         */
        assert(condition, description){
            if(this._shouldLog(levelOf.assert) && !condition){
                const message = {
                    namespace:this.name,
                    type:"assert",
                    message:["Assertion failed:",description],
                    time:new Date()
                };
                backlog.add(message);
                LogUtils.printToConsole("assert",
                    this._nameAndTime().concat(message.message), mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        _basicLogMethod(method, level, args){
            if(this._shouldLog(level)){
                const message = {namespace:this.name, type:method, message:args, time:new Date()};
                args = this._nameAndTime().concat(args);
                backlog.add(message);
                LogUtils.printToConsole(method, args, mode);
                LogUtils.execCallbacks(callbacks, message);
            }
        }

        /**
         * Displays a debug message in the console
         * @param {...*} var_args - displays anything you pass in
         * @example
         * logger.debug("hello world");
         * logger.debug("favorite number:", 7);
         * logger.debug(1, "h", {a:4,b:[1,2,3]});
         */
        debug(var_args){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("debug",levelOf.debug, args);
        }

        /**
         * Displays a log message in the console
         * @param {...*} var_args - displays anything you pass in
         * @example
         * logger.log("hello world");
         * logger.log("favorite number:", 7);
         * logger.log(1, "h", {a:4,b:[1,2,3]});
         */
        log(var_args){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("log",levelOf.log, args);
        }

        /**
         * Display the properties of an object in a tree
         * @param {object} obj - object to display properties of
         * @example
         * logger.dir({a:"Apple",b:["Brother","Bright"],c:{name:"Cat",remaining_lives:9}});
         */
        dir(obj){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("dir",levelOf.dir, args);
        }

        /**
         * Displays an info message in the console
         * @param {...*} var_args - displays anything you pass in
         * @example
         * logger.info("hello world");
         * logger.info("favorite number:", 7);
         * logger.info(1, "h", {a:4,b:[1,2,3]});
         */
        info(var_args){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("info",levelOf.info, args);
        }

        /**
         * Displays a warn message in the console
         * @param {...*} var_args - displays anything you pass in
         * @example
         * logger.warn("hello world");
         * logger.warn("favorite number:", 7);
         * logger.warn(1, "h", {a:4,b:[1,2,3]});
         */
        warn(var_args){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("warn",levelOf.warn, args);
        }

        /**
         * Displays an error message in the console and shows a stack trace
         * @param {...*} var_args - displays anything you pass in
         * @example
         * logger.error("hello world");
         * logger.error("favorite number:", 7);
         * logger.error(1, "h", {a:4,b:[1,2,3]});
         */
        error(var_args){
            const args = Array.prototype.slice.call(arguments);
            this._basicLogMethod("error",levelOf.error, args);
        }

        /**
         * Add a callback to be called on every message passed through every logger.
         *
         * @param {function} callback - function that takes a message object as input.
         * A message object has the following properties: namespace, type, time, message.
         *
         * @param {boolean} useBacklog - Will call function on up to 100 previous
         * messages when true. Optional.
         *
         * @returns {object} remover - Call remover.remove() to remove the callback
         * (no longer pass messages to it)
         *
         * @example
         * let messageTypes = [];
         * let callback = function(message){
         *     messageTypes.push(message.type);
         * };
         * let remover = logger.addCallback(callback, true);
         * // messageTypes now contains the types of the last 100 messages,
         * // because useBacklog was true
         * remover.remove();
         * // the callback will not be run on any messages after it has been removed
         */
        addCallback(callback, useBacklog){
            callbacks[nextCallbackId] = callback;
            if(useBacklog){
                backlog.applyCallback(callback);
            }

            let remover  = {};
            const position = nextCallbackId;
            remover.remove = () => {
                delete callbacks[position];
            };
            nextCallbackId ++;
            return remover;
        }

        /**
         * Creates a new logger instance under a new namespace.
         *
         * @param {string} name - namespace of new logger.
         *
         * @returns {object} logger - if a logger has already been instantiated with the same name,
         * an error will be displayed and the previously declared instance will be returned.
         *
         * @example
         * let myNewLogger = logger.createLogger("myNewLogger");
         * myNewLogger.log("hello from my new logger!");
         */
        createLogger(name){
            const newLogger = new Log(name);
            return newLogger.name ? newLogger : loggers[name];
        }

    }

    return new Log(defaultName);

});